import React, { useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Spinner } from "@resideo/blueprint-react";
import { useAuth, resolveLoginTarget } from "@/contexts/AuthContext";
import { ErrorDisplay } from "@/components/ErrorDisplay";
import { redirectToProPortal } from "@/config/proPortal";
import { preferredAccountTypeService } from "@/services/preferredAccountTypeService";
import styles from "./Auth0Callback.module.css";


/**
 * Auth0 Callback Component
 * Handles the redirect from Auth0 after authentication
 */
export const Auth0Callback: React.FC = () => {
  const { isAuthenticated, isLoading: auth0Loading, error: auth0Error, getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const { login, showInvalidCredentialsModal } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);
  // Set once a run has its token and is committed to carrying the callback
  // through to a terminal state (login / redirect / error). Until then a
  // torn-down run must hand the work back — see the cleanup below.
  const isCommittedRef = useRef(false);

  const retryLogin = () => {
    void loginWithRedirect().catch((loginError: unknown) => {
      console.error("Auth0 login error:", loginError);
    });
  };

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleCallback = async () => {
      // Prevent double execution - check and set immediately
      if (hasProcessedRef.current) {
        return;
      }
      hasProcessedRef.current = true;

      // Wait for Auth0 to finish loading
      if (auth0Loading) {
        hasProcessedRef.current = false; // Reset if still loading
        return;
      }

      // Handle Auth0 errors
      if (auth0Error) {
        console.error("❌ Auth0Callback: Auth0 error:", auth0Error);
        setError(`Authentication failed: ${auth0Error.message}`);
        return;
      }

      // Check if authenticated
      if (!isAuthenticated) {
        console.error("❌ Auth0Callback: Not authenticated");
        setError("Authentication failed. Please try again.");
        return;
      }

      // Set a timeout to prevent hanging indefinitely
      let timeoutTriggered = false;
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        timeoutTriggered = true;
        console.error("❌ Auth0Callback: Timeout - process took too long");
        setError("Authentication timeout. Please try again.");
      }, 30000); // 30 second timeout

      try {
        // Get access token
        const token = await getAccessTokenSilently();
        if (cancelled) return;
        isCommittedRef.current = true;

        // Store token for pilets
        localStorage.setItem("authToken", token);
        sessionStorage.setItem("authToken", token);

        // Store user info for pilets
        if (user) {
          localStorage.setItem("currentUser", JSON.stringify({
            id: user.sub,
            email: user.email,
            name: user.name,
            firstName: user.given_name,
            lastName: user.family_name,
            photoUrl: user.picture,
          }));
        }

        // Account types remain authoritative; preferences only affect routing.
        const apiServiceModule = await import("@/services/apiService");
        const apiService = apiServiceModule.default;

        const preferredAccountTypePromise = preferredAccountTypeService.get().catch((settingsError) => {
          console.warn("Failed to read preferred account type; using defaults", settingsError);
          return null;
        });

        let consumerAccountTypes: string[] = [];
        let partnerAccountTypes: string[] = [];
        let preferredAccountType: string | null = null;
        try {
          const [accountTypesData, savedPreference] = await Promise.all([
            apiService.getAccountTypes(),
            preferredAccountTypePromise,
          ]);
          preferredAccountType = savedPreference;
          consumerAccountTypes = accountTypesData?.data?.consumerAccountTypes || [];
          partnerAccountTypes = accountTypesData?.data?.partnerAccountTypes || [];
          if (!accountTypesData?.data) {
            clearTimeout(timeoutId);
            setError("We couldn't verify your account access. Please try again later.");
            return;
          }
        } catch (accountTypesErr) {
          console.error("❌ Auth0Callback: getAccountTypes failed:", accountTypesErr);
          clearTimeout(timeoutId);
          setError("We couldn't verify your account access. Please try again later.");
          return;
        }

        const loginTarget = resolveLoginTarget(
          consumerAccountTypes,
          partnerAccountTypes,
          preferredAccountType,
        );

        if (loginTarget === "UNSUPPORTED") {
          clearTimeout(timeoutId);
          if (timeoutTriggered) {
            return;
          }
          // Residential-only users should see the invalid-credentials modal
          // (existing behavior prior to recent preference-order changes).
          if (consumerAccountTypes.includes("RESIDENTIAL")) {
            showInvalidCredentialsModal();
          } else {
            setError("No supported accounts are available for this login.");
          }
          return;
        }

        // Service Professional accounts never enter One Portal — hand the
        // session over to Pro Portal instead.
        if (loginTarget === "PARTNER") {
          if (timeoutTriggered) {
            return;
          }
          // Use the new onBeforeNavigate callback so we can persist the
          // preferred account type on the server and wait for it to complete
          // before the browser navigates away.
          const redirected = await redirectToProPortal(async () => {
            try {
              await preferredAccountTypeService.set("PARTNER");
            } catch (prefErr) {
              console.warn("Failed to save preferred account type PARTNER", prefErr);
            }
          });

          // Clear timeout after redirect helper completes (either success or
          // failure). We keep the timeout active while the helper runs so the
          // overall callback doesn't hang indefinitely.
          clearTimeout(timeoutId);

          if (!redirected) {
            setError("We couldn't start your Pro Portal session. Please try again.");
          }
          return;
        }

    // Build lightweight contexts from account types (no role/permissions yet)
        const contexts: Array<{
          context: string;
          contextDisplayName: string;          
        }> = [];

        // Each consumer account type becomes its own context
        const consumerDisplayNames: Record<string, string> = {
        RESIDENTIAL: "Residential",
        COMMERCIAL: "Commercial",
        };
        for (const accountType of consumerAccountTypes) {
        contexts.push({
          context: accountType,
          contextDisplayName:
          consumerDisplayNames[accountType] || accountType,
        });
        }
 
        // Partner account types map to PARTNER context
        if (partnerAccountTypes.length > 0) {
        contexts.push({
          context: "PARTNER",
          contextDisplayName: "Partner", 
        });
        }

        // Check if user has any contexts
        if (contexts.length === 0) {
          console.error("❌ No contexts found for user");
          clearTimeout(timeoutId);
          setError("No accounts found. Please contact support.");
          return;
        }

         // Build user data (permissions will be loaded after context selection)
        const userData = {
        id: user?.sub || "",
        email: user?.email || "",
        name: user?.name || "",
        contexts: contexts.map((ctx) => ({
          context: ctx.context,
          contextDisplayName: ctx.contextDisplayName,
        })),
        };
        // Login with user data — AuthContext handles navigation
        await login(userData, loginTarget);

        // Clear the timeout since we succeeded
        clearTimeout(timeoutId);
        
        // Don't navigate if timeout already triggered
        if (timeoutTriggered) {
          return;
        }        
        
      } catch (err) {
        clearTimeout(timeoutId); // Clear timeout on error
        
        // Don't show error if timeout already handled it
        if (timeoutTriggered) {
          return;
        }

        console.error("❌ Auth0Callback: Error processing callback:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    void handleCallback();

    return () => {
      cancelled = true;
      // React StrictMode tears this effect down and immediately re-runs it in
      // development. A run abandoned before it was committed has done nothing,
      // so release the guard — otherwise the re-run is blocked by it, the
      // callback is never processed, and the app renders a blank page forever.
      if (!isCommittedRef.current) {
        hasProcessedRef.current = false;
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated, auth0Loading, auth0Error, getAccessTokenSilently, loginWithRedirect, user, login]);

  // Show error if present
  if (error) {
    return (
      <div className={styles.callbackContainer}>
        <ErrorDisplay message={error} />
        <button type="button" onClick={retryLogin}>
          Retry sign-in
        </button>
      </div>
    );
  }

  return (
    <main className={styles.loadingContainer} role="status" aria-label="Signing you in">
      <Spinner size="large" />
    </main>
  );
};